/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ModalProps } from 'antd/lib/modal';
import {
  Modal,
  Table,
  message,
  DatePicker,
  Switch,
  Tabs,
  Checkbox,
  Row,
  Col,
  TimePicker,
  Button,
  Spin,
} from 'antd';
import ModalLoader from '~components/atoms/ModalLoader/ModalLoader';

import IconButton from '~components/_common/IconButton/IconButton';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { arrToTree, TableEntry } from '~src/utils/securityGroupsConfig';
import { TableProps } from 'antd/lib/table';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { DbFolder } from '~src/types/db';
import { toggleFolderNotify } from '~src/services/api';
import { useProjectId } from '~src/hooks/useProjectId';
import { queryCache, useMutation } from 'react-query';
import { appLog } from '~src/utils/log';
import moment, { Moment, isMoment } from 'moment';
import { useUserSettings } from '~src/hooks/api-hooks/useUserSettings';
import { saveUserSettings, saveVacationSettings } from '~src/services/api/user';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';

interface NotificationModalProps extends Partial<ModalProps> {
  modalState?: boolean;
}

const getFrequencyData = (
  type: number,
  timeFirst: Moment,
  timeSecond: Moment
) => {
  const time_1 = timeFirst?.format('HH:mm');
  const time_2 = timeSecond?.format('HH:mm');
  switch (type) {
    case 1:
      return {
        frequency: type,
      };
    case 2:
      return {
        frequency: type,
        time_1,
      };
    case 5:
      return {
        frequency: type,
        time_1,
        time_2,
      };
    default:
      return {
        frequency: type,
      };
  }
};

export const NotificationModal: React.FC<NotificationModalProps> = ({
  visible,
  onCancel,
}) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const projectId = useProjectId();
  const userSettings = useUserSettings();
  const foldersQueryParams = {
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
    projectId: String(projectId),
  };

  const foldersQuery = useFolders(foldersQueryParams, { enabled: !!projectId });
  const tableData = useMemo(() => arrToTree(foldersQuery.data || [], null), [
    foldersQuery,
  ]);

  const initialTime = {
    time1: moment(
      userSettings.data?.time_1 === '00:00:00' &&
        userSettings.data?.time_2 === '00:00:00'
        ? '06:30'
        : userSettings.data?.time_1 || '06:30',
      'HH:mm'
    ),
    time2: moment(
      userSettings.data?.time_1 === '00:00:00' &&
        userSettings.data?.time_2 === '00:00:00'
        ? '14:30'
        : userSettings.data?.time_2 || '14:30',
      'HH:mm'
    ),
  };

  const [frequencyValues, setFrequencyValues] = useState<number[]>([
    userSettings.data?.frequency || 1,
  ]);
  const [timeValues, setTimeValues] = useState(initialTime);
  const [isModalLoading, setIsModalLoading] = useState(false);

  useEffect(() => {
    if (userSettings.data?.frequency)
      setFrequencyValues([userSettings.data?.frequency]);
    if (userSettings.data?.time_1)
      setTimeValues({
        ...timeValues,
        time1: moment(userSettings.data?.time_1, 'HH:mm'),
      });
    if (userSettings.data?.time_1 && userSettings.data?.time_2)
      setTimeValues(initialTime);
  }, [userSettings.data]);

  const [
    doUpdateFolderNotifications,
    updateFolderNotificationMutation,
  ] = useMutation(toggleFolderNotify, {
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify, is_recursive }, { folderId }) => {
      if (is_recursive) {
        queryCache.invalidateQueries('folders');
        queryCache.invalidateQueries('files');
      } else {
        queryCache.setQueryData<DbFolder[]>(
          ['folders', foldersQueryParams],
          (prevFolders = []) => {
            const changedFolderIndex = prevFolders.findIndex(
              (item) => item.id === folderId
            );
            if (changedFolderIndex >= 0) {
              const newList = [...prevFolders];
              newList[changedFolderIndex] = {
                ...newList[changedFolderIndex],
                is_notify,
              };
              return newList;
            }

            return prevFolders;
          }
        );
      }
    },
  });
  interface UpdateNotificationsRecursiveProps {
    folderId: DbFolder['id'];
    is_notify: boolean;
    arr: DbFolder[];
  }

  const updateNotificationsRecursive = ({
    folderId,
    is_notify,
    arr,
  }: UpdateNotificationsRecursiveProps) => {
    return arr.map((element, index) => {
      const newArr = [...arr];
      if (element.parent_id === folderId) {
        const newElement = { ...newArr[index], is_notify };
        newArr[index] = newElement;
      }
      return updateNotificationsRecursive({
        folderId: element.id,
        is_notify,
        arr: newArr,
      });
    });
  };

  const [doSaveVacationSettings] = useMutation(saveVacationSettings, {
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: (data) => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.setQueryData('userSettings', {
        ...data,
        vacation: data.vacation ? Number(data.vacation) * 1000 : data.vacation,
      });
    },
  });

  const [doSaveUserSettings] = useMutation(saveUserSettings, {
    onMutate: () => {
      setIsModalLoading(true);
    },
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      setIsModalLoading(false);
    },
    onSuccess: (data, variables) => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.setQueryData('userSettings', {
        ...userSettings.data,
        ...variables,
      });
      setIsModalLoading(false);
    },
  });

  const columns: TableProps<TableEntry>['columns'] = [
    {
      key: 'folderStructure',
      title: intl.formatMessage({ id: 'folder-structure' }),
      dataIndex: 'title',
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
        },
      }),
      render: (title, record) => {
        const isTenderFolder = record.dbFolder?.option?.is_tender_folder;
        return {
          children: isTenderFolder ? (
            <Fragment>
              <NavIcon
                customStyle={{ width: '24px', marginRight: '6px' }}
                icon={getTenderIconArichive(record.dbFolder?.option)}
              />
              {title}
            </Fragment>
          ) : (
            <Fragment>
              <IconButton
                icon={['fas', 'folder']}
                customStyle={style.folderIcon}
                containerStyle={style.folderIconContainer}
                backgroundOff
              />
              {title}
            </Fragment>
          ),
        };
      },
    },
    {
      key: 'notification',
      title: (
        <IconButton
          iconStyle="fad"
          icon="minus-circle"
          color="green"
          customStyle={style.small}
          backgroundOff
        />
      ),
      render: (value, record, index) => {
        return (
          <IconButton
            iconStyle={record.dbFolder.is_notify ? 'fad' : 'far'}
            icon={record.dbFolder.is_notify ? 'check-circle' : 'circle'}
            color={record.dbFolder.is_notify ? 'green' : 'darkGray'}
            customStyle={[style.small, { cursor: 'pointer' }]}
            click={() =>
              doUpdateFolderNotifications({
                projectId: Number(projectId),
                folderId: record.dbFolder.id,
                notify: !record.dbFolder.is_notify,
                recursive: true,
              })
            }
            backgroundOff
          />
        );
      },
      width: '12%',
    },
  ];

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'notifications' })}
          icon={['far', 'bell']}
        />
      }
      footer={null}>
      {/* TODO: add translation keys */}
      {isModalLoading && <ModalLoader />}
      <Tabs css={style.tabContainer}>
        <Tabs.TabPane
          key="general-tab"
          tab={intl.formatMessage({ id: 'general-tab' })}>
          <Fragment>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <h3 css={style.heading}>
                {intl.formatMessage({ id: 'global-settings' })}
              </h3>
              <IconButton
                icon={['far', 'info-circle']}
                customStyle={{ fontSize: '130%' }}
                containerStyle={{
                  marginLeft: 0,
                  color: '#7a829b',
                }}
                backgroundOff
                tooltip={
                  intl.locale === 'da' ? (
                    <div>
                      <h3 css={style.whiteHeader}>
                        Globale Notifikationsindstillinger
                      </h3>
                      <p>
                        Indstillingerne på denne side vil påvirke C-WEB Projekt
                        notifikationer for alle aktive projekter.
                      </p>
                      <p>
                        <b>Bloker alle notifikationer</b>
                        <br />
                        Hvis det ønskes at C-WEB ikke sender notifikationer fra
                        C-WEB Projekt kan de blokeres.
                      </p>
                      <p>
                        <b>Spring weekenden over</b>
                        <br />
                        Hvis denne indstilling er aktiv, vil notifikationer
                        lavet efter normal arbejdstid fredag blive sendt ved
                        normal arbejdstids begyndelse mandag morgen.
                      </p>
                      <p>
                        <b>Frekvens</b>
                        <br />
                        Straks - Alle notifikationer vil blive afsendt
                        øjeblikkeligt, når nye ændringer træder i kraft.
                        <br />
                        En gang dagligt - Alle notifikationer bliver samlet og
                        afsendt på det valgte tidspunkt én gang om dagen.
                        <br />
                        To gange dagligt - Alle notifikationer bliver samlet og
                        afsendt på det valgte tidspunkt to gange om dagen.
                      </p>
                      <p>
                        <b>Tidspunkt 1</b>
                        <br />
                        Ved valg af både “En gang dagligt” og “To gange dagligt”
                        vil notifikationer blive samlet og sendt ved det valgte
                        tidspunkt.
                      </p>
                      <p>
                        <b>Tidspunkt 2</b>
                        <br />
                        Ved valg af “To gange dagligt” vil det være muligt at
                        vælge et andet tidspunkt hvor notifikationer vil blive
                        udsendt på.
                      </p>
                      <p>
                        <b>Ferietilstand</b>
                        <br />
                        Hvis det ønskes at forsinke alle notifikationer til et
                        valgt tidspunkt i fremtiden, kan dette gøres ved
                        indstillingen “Ferietilstand”. Alle notifikationer
                        bliver sat i kø og vil blive afsendt på det valgte
                        tidspunkt.
                        <br />
                        Hvis “Ferietilstand” er aktiv, vil andre
                        notifikationsindstillinger blive tilsidesat.
                      </p>
                      <p>
                        <b>Information</b>
                        <br />
                        Typisk vil notifikationer blive modtaget indenfor få
                        minutter af de angivne tidspunkter.
                        <br />
                        Indstillingerne vil påvirke hvornår notifikationerne
                        bliver afsendt fra C-WEB Notifikations Server, ikke
                        hvornår de modtages i en mail klient. Hvis det er
                        vigtigt at notifikationer er tilgængelige på et bestemt
                        tidspunkt, vælg et tidspunkt 15 minutter før det ønskede
                        tidspunkt.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h3 css={style.whiteHeader}>
                        Global notification settings
                      </h3>
                      <p>
                        The settings on this page will affect C-WEB Projekt
                        notifications for all your active projects.
                      </p>
                      <p>
                        <b>Block all notifications</b>
                        <br />
                        If no notifications from C-WEB Projekt are wanted, it is
                        possible to toggle the Block all notifications trigger.
                      </p>
                      <p>
                        <b>Skip weekends</b>
                        <br />
                        If the toggle "Skip weekends" is on, all changes made
                        after normal work hours on Friday will be queued until
                        Monday morning.
                      </p>
                      <p>
                        <b>Frequency</b>
                        <br />
                        Instant - All notifications will be created and sent as
                        soon as changes are made.
                        <br />
                        Once a day - All notifications will be queued to be
                        delivered at the selected time every day.
                        <br />
                        Twice a day - All notifications will be queued to the
                        next closest of the two selected times twice a day.
                      </p>
                      <p>
                        <b>Time 1</b>
                        <br />
                        When both “Once a day” and “Twice a day” frequency is
                        selected, notifications will be queued to be sent at the
                        selected time.
                      </p>
                      <p>
                        <b>Time 2</b>
                        <br />
                        When “Twice a day” frequency is selected, this selected
                        time will be the second time a day where notifications
                        are queued to be sent.
                      </p>
                      <p>
                        <b>Vacation mode</b>
                        <br />
                        All notifications can be delayed to a selected date and
                        time in the future, this can be done by using the
                        “Vacation mode” settings. All changes will be queued to
                        be delivered at the selected time.
                        <br />
                        If Vacation mode is active, all other notification
                        settings will automatically be ignored.
                      </p>
                      <p>
                        <b>Information</b>
                        <br />
                        Notifications will typically be available in the email
                        client within a few minutes of the selected times.
                        <br />
                        The settings will affect when notifications are sent
                        from the notification server, not when the email client
                        will receive them. If it is important that notifications
                        are received at a specific time, ensure to select a time
                        15 minutes before the desired time of receival.
                      </p>
                    </div>
                  )
                }
                widthTooltip="600px"
              />
            </div>

            {/* BLOCK NOTIFICATIONS */}
            <div css={style.rowContainer}>
              <span css={{ display: 'inline-block', width: 170 }}>
                {intl.formatMessage({ id: 'block-notifications' })}
              </span>
              <Switch
                // disabled={!vacation}
                checked={userSettings.data?.block_notification}
                onChange={(value) =>
                  doSaveUserSettings({ block_notification: value })
                }
              />
            </div>
            {/* SKIP WEEKENDS */}
            {!userSettings.data?.block_notification && (
              <Fragment>
                <div css={[style.rowContainer, { marginTop: '6px' }]}>
                  <span css={{ display: 'inline-block', width: 170 }}>
                    {intl.formatMessage({ id: 'skip-weekends' })}
                  </span>
                  <Switch
                    checked={!userSettings.data?.is_send_weekend}
                    onChange={(value) =>
                      doSaveUserSettings({ is_send_weekend: !value })
                    }
                  />
                </div>

                {/* FREQUENCY BLOCK */}
                <div css={style.rowContainer}>
                  <span
                    css={{
                      display: 'inline-block',
                      width: 140,
                      marginTop: '6px',
                    }}>
                    {intl.formatMessage({ id: 'frequency' })}
                  </span>
                </div>
                <Checkbox.Group
                  style={{ width: '100%', marginTop: '10px' }}
                  value={frequencyValues}
                  onChange={(checkedValues) => {
                    const lastValue = checkedValues[checkedValues.length - 1];
                    if (!lastValue) return;
                    setFrequencyValues([lastValue as number]);
                  }}>
                  <Row>
                    <Col span={8}>
                      <div css={style.checkboxContainer}>
                        {intl.formatMessage({ id: 'instant' })}
                        <Checkbox value={1} />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div css={style.checkboxContainer}>
                        {intl.formatMessage({ id: 'once-a-day' })}
                        <Checkbox value={2} />
                      </div>
                    </Col>
                    <Col span={8}>
                      <div css={style.checkboxContainer}>
                        {intl.formatMessage({ id: 'twice-a-day' })}
                        <Checkbox value={5} />
                      </div>
                    </Col>
                  </Row>
                </Checkbox.Group>
                <div css={{ display: 'flex' }}>
                  <div>
                    {(frequencyValues[0] === 2 || frequencyValues[0] === 5) && (
                      <Fragment>
                        <div css={[style.rowContainer, { marginTop: 10 }]}>
                          <span css={{ display: 'block', width: 140 }}>
                            {intl.formatMessage({ id: 'time-1' })}
                          </span>
                        </div>
                        <div css={[style.rowContainer, { marginTop: 10 }]}>
                          <TimePicker
                            clearIcon={false}
                            value={timeValues.time1}
                            onChange={(value) => {
                              if (!value) return;
                              setTimeValues({ ...timeValues, time1: value });
                            }}
                            format={'HH:mm'}
                          />
                        </div>
                      </Fragment>
                    )}
                    {frequencyValues[0] === 5 && (
                      <Fragment>
                        <div css={[style.rowContainer, { marginTop: 10 }]}>
                          <span css={{ display: 'block', width: 140 }}>
                            {intl.formatMessage({ id: 'time-2' })}
                          </span>
                        </div>
                        <div css={[style.rowContainer, { marginTop: 10 }]}>
                          <TimePicker
                            clearIcon={false}
                            value={timeValues.time2}
                            onChange={(value) => {
                              if (!value) return;
                              setTimeValues({ ...timeValues, time2: value });
                            }}
                            format={'HH:mm'}
                          />
                        </div>
                      </Fragment>
                    )}
                  </div>

                  <div
                    css={[
                      style.rowContainer,
                      {
                        justifyContent: 'flex-end',
                        width: '100%',
                        marginTop: frequencyValues[0] === 1 ? '20px' : 'auto',
                      },
                    ]}>
                    <Button
                      loading={isModalLoading}
                      type="primary"
                      onClick={() => {
                        doSaveUserSettings(
                          getFrequencyData(
                            frequencyValues[0],
                            timeValues.time1,
                            timeValues.time2
                          )
                        );
                      }}>
                      {intl.formatMessage({ id: 'save' })}
                    </Button>
                  </div>
                </div>

                {/* VACATION MODE */}

                <div css={style.border}></div>
                <div css={[style.rowContainer, { marginTop: 10 }]}>
                  <div css={{ display: 'inline-block', width: 40 }}>
                    <IconButton
                      icon={['far', 'plane']}
                      customStyle={style.planeIcon}
                      backgroundOff
                    />
                  </div>
                  <h3 css={[style.heading, { paddingBottom: '4px' }]}>
                    {intl.formatMessage({ id: 'vacation-mode' })}
                  </h3>
                </div>
                <div css={[style.rowContainer, { marginTop: 10 }]}>
                  <DatePicker
                    disabledDate={(date) => date <= moment()}
                    showTime
                    value={
                      userSettings.data?.vacation
                        ? moment(userSettings.data?.vacation)
                        : null
                    }
                    onChange={(value) => {
                      doSaveVacationSettings({
                        vacation: value?.unix() || null,
                      });
                    }}
                  />
                </div>
              </Fragment>
            )}
          </Fragment>
        </Tabs.TabPane>
        {!!projectId ? (
          <Tabs.TabPane
            key="project-tab"
            tab={intl.formatMessage({ id: 'project-tab' })}>
            <Fragment>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h3 css={style.heading}>
                  {intl.formatMessage({ id: 'project-settings' })}
                </h3>
                <IconButton
                  icon={['far', 'info-circle']}
                  customStyle={{ fontSize: '130%' }}
                  containerStyle={{
                    marginLeft: 0,
                    color: '#7a829b',
                  }}
                  backgroundOff
                  tooltip={
                    intl.locale === 'da' ? (
                      <div>
                        <h3 css={style.whiteHeader}>
                          Notifikationsindstillinger for mapperne på projektet
                        </h3>
                        <p>
                          Det kan vælges her hvilke mapper der ønskes
                          notifikationer fra.
                          <br />
                          Der vil blive notificeret om alle ændringer i de
                          tilmeldte mapper. Alle nyoprettede mapper vil
                          automatisk blive tilmeldt notifikationer.
                        </p>
                        <p>
                          <b>Indstilling</b>
                          <br />
                          Benyt markøren til højre for en mappe for at
                          til-/afmelde notifikationer. Undermapper til den
                          valgte mappe vil automatisk arve den valgte
                          indstilling.
                          <br />
                          Benyt plus/minus symbolerne til venstre for mapperne
                          for at åbne mappestrukturen og vælge mere specifikke
                          notifikationsindstillinger for de enkelte undermapper.
                        </p>
                        <p>
                          <b>Information</b>
                          <br />
                          Vær opmærksom på at notifikationsindstillinger valgt
                          for enkelte filer stadig er i effekt selvom der er
                          fravalgt notifikationer for mappen de er i.
                          <br />
                          Benyt notifikations ikonet på en fil i en mappe for at
                          aktivere notifikationer til en enkelt fil i en mappe
                          der ikke ønskes notifikationer fra.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <h3 css={style.whiteHeader}>
                          Notification settings for the folders on the project
                        </h3>
                        <p>
                          It is possible to customize what folders should
                          trigger notifications from this menu.
                          <br />
                          Notifications will be queued for all changes within
                          the activated folders. Newly created folders will
                          automatically be subscribed to notifications.
                        </p>
                        <p>
                          <b>Settings</b>
                          <br />
                          The marker at the right of the folders will indicate
                          if the folders are being subscribed to or not.
                          Subfolders will automatically inherit the chosen
                          setting.
                          <br />
                          The plus and minus symbols can be used to open the
                          folder structure to be able to subscribe/unsubscribe
                          to specific subfolders.
                        </p>
                        <p>
                          <b>Information</b>
                          <br />
                          Specific chosen notification settings for files will
                          still be in effect even though the folder has been
                          unsubscribed.
                          <br />
                          Use the notification icon on a file in the explorer
                          view to subscribe to a single file within an
                          unsubscribed folder.
                        </p>
                      </div>
                    )
                  }
                  widthTooltip="500px"
                />
              </div>
              <Table<TableEntry>
                showSorterTooltip={false}
                dataSource={tableData}
                pagination={false}
                columns={columns}
                scroll={{ y: '50vh' }}
                loading={foldersQuery.isLoading || foldersQuery.isFetching}
              />
            </Fragment>
          </Tabs.TabPane>
        ) : null}
      </Tabs>

      {/* TODO: add translation keys */}
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  chevronIcon: {
    color: theme.colors.secondary,
    marginTop: '-5px',
  },

  titleBtn: {
    color: theme.colors.secondary,
    fontSize: '20px',
  },
  closeBtn: {
    color: theme.colors.darkGray,
    marginTop: '18px',
    fontSize: '20px',
    transition: '0.3s',
  },
  green: {
    color: true ? theme.colors.green : theme.colors.lightGray,
  },
  darkGray: {
    color: true ? theme.colors.darkGray : theme.colors.lightGray,
  },
  small: {
    fontSize: '16px',
  },
  folderIcon: {
    color: theme.colors.yellow,
    fontSize: 18,
    marginTop: -7,
  },
  folderIconContainer: {
    minWidth: 20,
    marginRight: 7,
  },
  tabContainer: {
    padding: '0 15px',
    marginTop: -40,
  },
  rowContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  heading: {
    marginTop: 10,
  },
  whiteHeader: {
    color: 'white',
  },
  border: {
    margin: '15px 0',
    padding: 5,
    borderBottom: '2px solid black',
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  planeIcon: {
    color: theme.colors.primary,
    fontSize: '22px',
  },
});
